import React, { Suspense, lazy } from 'react'
import { Box, CircularProgress, Dialog, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { CameraScanDialogProps } from './types'
import { dialogHeaderSx, dialogPaperSx, feedbackPanelSx } from './CameraScan.styles'

/**
 * Unico punto donde entra ZXing. React solo evalua este import cuando renderiza
 * el elemento, o sea cuando el dialogo se abre por primera vez (el Dialog de MUI
 * no monta sus hijos hasta entonces porque keepMounted es false por default).
 *
 * El .then remapea el named export a default, que es lo que espera React.lazy.
 */
const CameraScannerView = lazy(() =>
  import('./CameraScannerView').then((module) => ({
    default: module.CameraScannerView
  }))
)

const ScannerLoading = () => (
  <Box sx={feedbackPanelSx}>
    <CircularProgress sx={{ color: '#fff' }} />
    <Typography variant='body2'>Preparando la cámara…</Typography>
  </Box>
)

/**
 * Shell del escaner: cabecera + boton de cerrar, todo eager. El limite de
 * Suspense va adentro del contenido y no envolviendo al Dialog, para que el
 * shell aparezca instantaneo al tap y solo el area del video muestre el spinner
 * mientras baja el chunk. Envolviendo el Dialog habria dos transiciones de MUI
 * encadenadas y un flash negro.
 */
export const CameraScanDialog: React.FC<CameraScanDialogProps> = ({
  open,
  title,
  helperText,
  formats,
  timeoutMs,
  torchDelayMs,
  restrictToFrame,
  scanRegion,
  debug,
  onClose,
  onDecoded,
  onError
}) => (
  <Dialog fullScreen open={open} onClose={onClose} slotProps={{ paper: { sx: dialogPaperSx } }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={dialogHeaderSx}>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: '#fff' }} aria-label='Cerrar'>
          <CloseIcon />
        </IconButton>
      </Box>

      <Suspense fallback={<ScannerLoading />}>
        <CameraScannerView
          active={open}
          helperText={helperText}
          formats={formats}
          timeoutMs={timeoutMs}
          torchDelayMs={torchDelayMs}
          restrictToFrame={restrictToFrame}
          scanRegion={scanRegion}
          debug={debug}
          onDecoded={onDecoded}
          onClose={onClose}
          onError={onError}
        />
      </Suspense>
    </Box>
  </Dialog>
)
